<script setup lang="ts">
import { computed, useId } from 'vue'
import DOMPurify from 'dompurify'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from '@/components/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/tooltip'
import type { StandardSummary } from '@/standards/api'
import { SELECT_TRIGGER_STATE_CLASSES } from '@/browse/select_trigger_classes'

const props = defineProps<{
  standards: StandardSummary[]
  selectedStandardLongRef: string
  isLoading: boolean
  labelledBy: string
}>()

const emit = defineEmits<{ select: [standardLongRef: string] }>()

// Choosing this value clears the standard filter.
const NO_STANDARD = ''

const selectedStandard = computed(() => props.standards.find((standard) => standard.standard_long_ref === props.selectedStandardLongRef))
const sanitizedStandards = computed(() =>
  props.standards.map((standard) => ({ ...standard, standard_text: DOMPurify.sanitize(standard.standard_text) }))
)
const hasNoStandards = computed(() => props.standards.length === 0)
const triggerLabel = computed(() => selectedStandard.value?.standard_short_ref ?? 'Filter by Standard')

function selectStandard(standardLongRef: unknown) {
  emit('select', String(standardLongRef))
}

const valueId = useId()
const optionClass = 'block max-w-[300px] cursor-pointer border-b-2 border-gray-200 px-5 py-1.5 text-gray-900 hover:bg-blue-400 hover:text-white data-[highlighted]:bg-blue-400 data-[highlighted]:text-white'
</script>

<template>
  <div>
    <span v-if="isLoading" class="text-text-md text-gray-700">
      <span class="material-icons-round animate-spin align-middle text-[1em]" aria-hidden="true">autorenew</span>
      Loading Standards...
    </span>
    <span v-else-if="hasNoStandards" class="text-text-md italic text-gray-700">No applicable standards</span>

    <DropdownMenu :modal="false">
      <DropdownMenuTrigger
        :aria-labelledby="`${labelledBy} ${valueId}`"
        class="flex w-full items-center justify-between rounded-xs border bg-white px-3 py-1.5 text-left text-text-md text-gray-900 focus-visible:outline-offset-2"
        :class="SELECT_TRIGGER_STATE_CLASSES"
      >
        <span :id="valueId" class="truncate">{{ triggerLabel }}</span>
        <span class="material-icons-round text-[1em]" aria-hidden="true">arrow_drop_up</span>
      </DropdownMenuTrigger>

      <DropdownMenuContent side="top" align="start" class="max-h-[400px] overflow-y-auto bg-white p-0">
        <TooltipProvider>
          <!-- reka gives the menu and its items tabindex -1, so axe needs a focusable element inside the scroll area (scrollable-region-focusable) -->
          <DropdownMenuRadioGroup tabindex="0" :model-value="selectedStandardLongRef" @update:model-value="selectStandard">
            <DropdownMenuRadioItem :value="NO_STANDARD" :class="optionClass">Don't filter by standard</DropdownMenuRadioItem>
            <Tooltip v-for="standard in sanitizedStandards" :key="standard.standard_long_ref">
              <TooltipTrigger as-child>
                <DropdownMenuRadioItem :value="standard.standard_long_ref" :class="optionClass">
                  <span class="block text-text-sm font-bold">{{ standard.standard_short_ref }}</span>
                  <span class="block truncate text-text-sm" v-html="standard.standard_text" />
                </DropdownMenuRadioItem>
              </TooltipTrigger>
              <TooltipContent side="right" class="max-w-xs bg-white text-gray-900 shadow-lg">
                <span class="block font-bold">{{ standard.standard_short_ref }}</span>
                <span v-html="standard.standard_text" />
              </TooltipContent>
            </Tooltip>
          </DropdownMenuRadioGroup>
        </TooltipProvider>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>
